// SNIGJ - Admin Currency & Exchange Rates Management
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  DollarSign, Euro, Wallet, RefreshCw, Save, Clock, TrendingUp, 
  Calculator, ArrowRight, AlertCircle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'Dollar américain', nameEn: 'US Dollar', flag: '🇺🇸' },
  { code: 'EUR', symbol: '€', name: 'Euro', nameEn: 'Euro', flag: '🇪🇺' },
  { code: 'CDF', symbol: 'FC', name: 'Franc congolais', nameEn: 'Congolese Franc', flag: '🇨🇩' }
];

const AdminCurrencyPage = () => {
  const { language } = useAuth();
  const [rates, setRates] = useState({
    USD: 1,
    EUR: 0.92,
    CDF: 2750
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [conversionAmount, setConversionAmount] = useState(100);
  const [fromCurrency, setFromCurrency] = useState('USD');

  useEffect(() => {
    loadRates();
  }, []);

  const loadRates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/currency-rates');
      if (response.data.rates) {
        setRates(response.data.rates);
      }
      setLastUpdated(response.data.updated_at);
      setUpdatedBy(response.data.updated_by);
    } catch (error) {
      console.error('Failed to load rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRates = async () => {
    setSaving(true);
    try {
      await api.put('/admin/currency-rates', {
        base_currency: 'USD',
        rates: rates
      });
      toast.success(language === 'fr' ? 'Taux de change mis à jour' : 'Exchange rates updated');
      loadRates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving rates');
    } finally {
      setSaving(false);
    }
  };

  const handleRateChange = (currency, value) => {
    const numValue = parseFloat(value) || 0;
    setRates(prev => ({
      ...prev,
      [currency]: numValue
    }));
  };

  const convertAmount = (amount, from, to) => {
    if (from === to) return amount;
    const fromRate = rates[from] || 1;
    const toRate = rates[to] || 1;
    // Convert to USD first, then to target
    const inUSD = amount / fromRate;
    return inUSD * toRate;
  };

  const formatCurrency = (amount, currencyCode) => {
    const currency = CURRENCIES.find(c => c.code === currencyCode);
    if (currencyCode === 'CDF') {
      return `${Math.round(amount).toLocaleString()} ${currency?.symbol || 'FC'}`;
    }
    return `${currency?.symbol || '$'}${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="h-8 w-64 skeleton" />
          <div className="grid grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-40 skeleton" />)}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl" data-testid="admin-currency-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-primary" />
              {language === 'fr' ? 'Gestion des Taux de Change' : 'Exchange Rate Management'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Définissez les taux de conversion entre USD, EUR et CDF' 
                : 'Set conversion rates between USD, EUR and CDF'}
            </p>
          </div>
          <Button onClick={handleSaveRates} disabled={saving} className="gap-2">
            <Save className="w-4 h-4" />
            {saving ? (language === 'fr' ? 'Enregistrement...' : 'Saving...') : (language === 'fr' ? 'Enregistrer' : 'Save')}
          </Button>
        </div>

        {/* Last Update Info */}
        {lastUpdated && (
          <Card className="bg-muted/50">
            <CardContent className="p-4 flex items-center gap-4">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm">
                  <span className="text-muted-foreground">
                    {language === 'fr' ? 'Dernière mise à jour:' : 'Last updated:'}
                  </span>{' '}
                  <span className="font-medium">
                    {new Date(lastUpdated).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Exchange Rates Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CURRENCIES.map((currency) => (
            <Card key={currency.code} className={currency.code === 'USD' ? 'border-primary' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{currency.flag}</span>
                    <span>{currency.code}</span>
                  </div>
                  {currency.code === 'USD' && (
                    <Badge variant="secondary">
                      {language === 'fr' ? 'Devise de base' : 'Base Currency'}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' ? currency.name : currency.nameEn}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-bold">{currency.symbol}</span>
                    {currency.code === 'USD' ? (
                      <div className="flex-1 text-center">
                        <span className="text-2xl font-mono">1.00</span>
                        <p className="text-xs text-muted-foreground">
                          {language === 'fr' ? '(Référence)' : '(Reference)'}
                        </p>
                      </div>
                    ) : (
                      <Input
                        type="number"
                        step="0.01"
                        value={rates[currency.code]}
                        onChange={(e) => handleRateChange(currency.code, e.target.value)}
                        className="flex-1 text-xl font-mono text-center"
                        data-testid={`rate-input-${currency.code}`}
                      />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    1 USD = {rates[currency.code]} {currency.symbol}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Conversion Calculator */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              {language === 'fr' ? 'Calculateur de conversion' : 'Conversion Calculator'}
            </CardTitle>
            <CardDescription>
              {language === 'fr' 
                ? 'Testez les conversions avec les taux actuels' 
                : 'Test conversions with current rates'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1">
                <Label className="mb-2 block">{language === 'fr' ? 'Montant' : 'Amount'}</Label>
                <Input
                  type="number"
                  value={conversionAmount}
                  onChange={(e) => setConversionAmount(parseFloat(e.target.value) || 0)}
                  className="text-lg"
                />
              </div>
              <div className="flex-1">
                <Label className="mb-2 block">{language === 'fr' ? 'Devise source' : 'Source Currency'}</Label>
                <div className="flex gap-2">
                  {CURRENCIES.map((c) => (
                    <Button
                      key={c.code}
                      variant={fromCurrency === c.code ? 'default' : 'outline'}
                      onClick={() => setFromCurrency(c.code)}
                      className="flex-1"
                    >
                      {c.flag} {c.code}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            
            <Separator className="my-4" />
            
            <div className="grid grid-cols-3 gap-4">
              {CURRENCIES.map((currency) => (
                <div 
                  key={currency.code} 
                  className={`p-4 rounded-lg text-center ${
                    currency.code === fromCurrency 
                      ? 'bg-primary/10 border-2 border-primary' 
                      : 'bg-muted'
                  }`}
                >
                  <p className="text-sm text-muted-foreground mb-1">{currency.flag} {currency.code}</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(convertAmount(conversionAmount, fromCurrency, currency.code), currency.code)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Information Card */}
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 dark:text-blue-100">
                  {language === 'fr' ? 'Information importante' : 'Important Information'}
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  {language === 'fr' 
                    ? 'Les taux de change définis ici seront utilisés dans tout le système pour l\'affichage des montants. Les citoyens pourront voir leurs frais et paiements dans la devise de leur choix.'
                    : 'Exchange rates set here will be used throughout the system for displaying amounts. Citizens will be able to see their fees and payments in their preferred currency.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminCurrencyPage;
